import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index';
import LovedBy from '../LovedBy';
import useTurnstile from '../../custom-hooks/useTurnstile';
import { checkRateLimit, formatWaitTime, getRateLimitReset } from '../../utils/rateLimiter';
import { message } from '../Message';
import { validateAndSanitizeEmail } from '../../utils/security';

const RECAPTCHA = process.env.REACT_APP_RECAPTCHA;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function QuickSignUpEmailAndGoogle({ onShowLogin, handleSubmitWithEmailLink, error, handleSubmitWithGoogle, isSubmitted, onSubmitted }) {
  const [currentLanguage, setCurrentLanguage] = useState('');
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isGoogleLoginFlow, setIsGoogleLoginFlow] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [initializeTurnstile, setInitializeTurnstile] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);

  const { t } = useTranslation();
  const changeLanguage = lng => {
    i18n.changeLanguage(lng);
  };

  // Initialize Turnstile only when user interacts with email input
  const { containerRef, token, isLoading: isTurnstileLoading, error: turnstileError } = useTurnstile({
    siteKey: initializeTurnstile ? TURNSTILE_SITE_KEY : null,
    theme: 'auto',
    size: 'normal',
  });

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (isSubmitted && loading) {
      setLoading(false);
    }
  }, [isSubmitted, loading]);

  useEffect(() => {
    // Add event listener to close the popup when clicked outside
    const handleClickOutside = event => {
      if (popupRef.current && event.target.contains(popupRef.current)) {
        onShowLogin(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
  }, []);

  // Initialize Turnstile when user clicks on email input (only if not already verified)
  const handleEmailInputFocus = () => {
    if (TURNSTILE_SITE_KEY && !token) {
      if (!initializeTurnstile) {
        setInitializeTurnstile(true);
        setShowTurnstile(true);
      } else if (!showTurnstile) {
        setShowTurnstile(true);
      }
    }
  };

  // Hide Turnstile when verification is successful
  useEffect(() => {
    if (token && showTurnstile) {
      setShowTurnstile(false);
    }
  }, [token, showTurnstile]);

  // Show Turnstile again only if there's an error and no valid token
  useEffect(() => {
    if (turnstileError && !token && initializeTurnstile && TURNSTILE_SITE_KEY) {
      setShowTurnstile(true);
    }
  }, [turnstileError, token, initializeTurnstile]);

  // Handle email change
  const handleEmailChange = (e) => {
    const rawEmail = e.target.value;
    
    // Validate and sanitize email input
    const { isValid, sanitized, error } = validateAndSanitizeEmail(rawEmail);
    
    // Set the sanitized value
    setEmail(sanitized);
    setEmailError('');

    // If validation failed during typing, show error only if user typed enough
    if (!isValid && sanitized.length > 3 && error) {
      setEmailError(error);
      return;
    }
  };

  const onSubmitWithEmailLink = (e) => {
    e.preventDefault();
    if (loading) return;

    if (!email) {
      setEmailError(t('message.error.emptyEmail'));
      return;
    }

    // Comprehensive security validation
    const validation = validateAndSanitizeEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error || t('message.error.validEmail'));
      return;
    }

    // Use sanitized email for all operations
    const safeEmail = validation.sanitized;

    // Check Turnstile token (only if site key is configured)
    if (TURNSTILE_SITE_KEY && !token) {
      message.error(t('message.error.completeCaptcha'));
      return;
    }

    // Client-side rate limiting
    if (!checkRateLimit('email-signup', safeEmail, { maxAttempts: 3, windowMs: 15 * 60 * 1000 })) {
      const resetTime = getRateLimitReset('email-signup', safeEmail, 15 * 60 * 1000);
      message.error(
        t('message.error.tooManyAttempts', { 
          time: formatWaitTime(resetTime) 
        })
      );
      return;
    }

    if (safeEmail.toLowerCase().endsWith('@gmail.com')) {
      setLoading(true);
      setIsGoogleLoginFlow(true);
      setTimeout(() => {
        if (handleSubmitWithGoogle) {
          handleSubmitWithGoogle({ preventDefault: () => {} }, t);
        }
        setLoading(false);
        setIsGoogleLoginFlow(false);
      }, 150);
      return;
    }

    setLoading(true);
    handleSubmitWithEmailLink(e, t, currentLanguage, safeEmail, token);
    // onSubmitted(true);
  };

  const handleGoogleFromSuccessMessage = (e) => {
    if (handleSubmitWithGoogle) {
      handleSubmitWithGoogle(e, t);
    }
  };

  const handleGoogleSignIn = (e) => {
    e.preventDefault();

    if (handleSubmitWithGoogle) {
      handleSubmitWithGoogle(e, t);
    }
  };

  return (
    <div className="pricing-showbox inset-0 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
      <div className="sign_in-container">
        <div ref={popupRef} className="dark-login-placeholder shadow-lg">
          <div className="login-form-container pt-2 pb-4">
            <div>
              {/* <h1 className="auth-panel-title" style={{ color: "#fff" }}>{t('signup.title')}</h1> */}
              <a href="#" className="px-3 py-3 link-block-6 w-inline-block signup-button" onClick={handleGoogleSignIn}>
                <div>
                  <img src="fonts/6373e8bb67056287b1380aa8_image%206.svg" loading="lazy" alt="" className="signup-button" />
                </div>
                <p className="body-1-semi-bold ml-12 signup-button" style={{ color: "#fff" }}>{t('button.googleSignup')}</p>
              </a>
            </div>
            <div className='mt-2' />
            <div className="sign-with-box mt-2">
              <div className="sign-with-divider" />
              <p className='pl-2 pr-2'>{t('signin.another')}</p>
              <div className="sign-with-divider" />
            </div>
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center mb-2">
                <hr className="w-full border-t border-gray-700 mb-2" />
                <div className="flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-white font-semibold">&nbsp;{t('giveAway.emailSent')}</span>
                </div>
                <h2 className="text-center text-2xl font-bold">
                  <div className="mx-auto relative z-[10] text-center mx-auto md:text-center label-giveaway">
                    <span>{t('giveAway.emailSentTitle')}</span>&nbsp;🎉
                  </div>
                </h2>
                <p className="text-gray-400 text-center">
                  {t('giveAway.emailSentSubPrefix')}{' '}
                  <button
                    type="button"
                    onClick={handleGoogleFromSuccessMessage}
                    disabled={loading}
                    className="text-blue-400 underline underline-offset-2 hover:text-blue-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {t('giveAway.googleWord')}
                  </button>{' '}
                  {t('giveAway.emailSentSubSuffix')}
                </p>
              </div>
            ) : (
              <>
                <div className='mt-2'>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={handleEmailInputFocus}
                    onClick={handleEmailInputFocus}
                    placeholder={t('giveAway.enterYourEmail')}
                    maxLength={254}
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    className="w-full px-4 py-4 rounded-md border border-gray-700 mb-2 focus:outline-none text-white placeholder-gray-500" style={{ backgroundColor: '#151515' }}
                  />
                  {emailError && <p className="text-red text-sm mb-2">{emailError}</p>}
                  
                  {/* Cloudflare Turnstile CAPTCHA - show on email click, hide when verified */}
                  {TURNSTILE_SITE_KEY && (
                    <>
                      <div style={{ 
                        display: showTurnstile ? 'flex' : 'none', 
                        justifyContent: 'center', 
                        marginTop: '16px', 
                        marginBottom: '16px' 
                      }}>
                        <div ref={containerRef} />
                      </div>
                      
                      {turnstileError && showTurnstile && !isTurnstileLoading && (
                        <p className="text-red text-sm mb-2 text-center">
                          {turnstileError}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={(e) => onSubmitWithEmailLink(e)}
                  disabled={
                    loading || 
                    (TURNSTILE_SITE_KEY && (isTurnstileLoading || !token))
                  }
                  className="w-full bg-pink-500 text-white py-4 text-sm rounded-md hover:bg-yellow-400 hover:scale-105 transition duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? (isGoogleLoginFlow ? t('giveAway.signingInWithGoogle') : t('giveAway.submitting'))
                    : t('button.emailSignup')}
                </button>
                <div className="forget-passwrod-form w-form">
                  <form id="email-form" name="email-form" data-name="Email Form">
                    <div className="_2nd-form" style={{ color: "#fff" }}>
                      <p className="agree-service" style={{ color: "#c2c2c2" }}>
                        {t('tos.agree')}
                        <a
                          className="agree-service" style={{ color: "#c2c2c2" }}
                          href={currentLanguage === 'en' ? '/legal/?view=terms' : `/${currentLanguage}/legal/?view=terms`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('footer.terms')}
                        </a>
                        {' '}&{' '}
                        <a
                          className="agree-service" style={{ color: "#c2c2c2" }}
                          href={currentLanguage === 'en' ? '/legal/?view=privacy-policy' : `/${currentLanguage}/legal/?view=privacy-policy`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('footer.privacy')}
                        </a>
                      </p>
                      <LovedBy t={t} />
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

QuickSignUpEmailAndGoogle.propTypes = {
  handleChange: PropTypes.func.isRequired,
  error: PropTypes.object,
};

export default QuickSignUpEmailAndGoogle;
